import { useState, useEffect } from 'react';
import { cachedFetchJSON } from '../utils/fetchCache';

const CENSUS_BASE = 'https://api.census.gov/data';
const API_KEY = process.env.REACT_APP_CENSUS_API_KEY;

function parseVars(varExpr) {
  if (!varExpr) return [];
  return varExpr.split('+').map(v => v.trim());
}

function computeValue(obj, numVars, denVars, unit) {
  const numerator = numVars.reduce((sum, v) => sum + (parseFloat(obj[v]) || 0), 0);
  const denominator = denVars.length > 0
    ? denVars.reduce((sum, v) => sum + (parseFloat(obj[v]) || 0), 0)
    : null;

  let value;
  if (denominator !== null && denominator > 0) {
    value = unit === 'percent' ? (numerator / denominator) * 100 : numerator / denominator;
  } else if (denominator === null) {
    value = numerator;
  } else {
    value = null;
  }
  return value !== null && value < 0 ? null : value;
}

/**
 * Fetches ACS 5-year data for all tracts in the configured county.
 * Returns [{ geoid, name, value }]
 */
async function fetchIndicatorYear(indicator, year, geo) {
  const numVars = parseVars(indicator.numeratorVar);
  const denVars = parseVars(indicator.denominatorVar);
  const allVars = [...new Set([...numVars, ...denVars])];

  const params = new URLSearchParams({
    get: ['NAME', ...allVars].join(','),
    for: 'tract:*',
    in: `state:${geo.state} county:${geo.county}`,
    ...(API_KEY ? { key: API_KEY } : {})
  });

  const rows = await cachedFetchJSON(`${CENSUS_BASE}/${year}/acs/acs5?${params}`);
  const headers = rows[0];
  return rows.slice(1).map(row => {
    const obj = {};
    headers.forEach((h, i) => { obj[h] = row[i]; });
    return {
      geoid: `${obj.state}${obj.county}${obj.tract}`,
      name: obj.NAME,
      value: computeValue(obj, numVars, denVars, indicator.unit)
    };
  });
}

/**
 * Fetches a single aggregate value at county or state level.
 * Returns a number or null.
 */
async function fetchAggregateValue(indicator, year, geo, level) {
  const numVars = parseVars(indicator.numeratorVar);
  const denVars = parseVars(indicator.denominatorVar);
  const allVars = [...new Set([...numVars, ...denVars])];

  const paramObj = {
    get: ['NAME', ...allVars].join(','),
    ...(API_KEY ? { key: API_KEY } : {})
  };
  if (level === 'state') {
    paramObj.for = `state:${geo.state}`;
  } else {
    paramObj.for = `county:${geo.county}`;
    paramObj.in = `state:${geo.state}`;
  }

  let rows;
  try {
    rows = await cachedFetchJSON(`${CENSUS_BASE}/${year}/acs/acs5?${new URLSearchParams(paramObj)}`);
  } catch (_) { return null; }
  if (!Array.isArray(rows) || rows.length < 2) return null;

  const headers = rows[0];
  const obj = {};
  headers.forEach((h, i) => { obj[h] = rows[1][i]; });
  return computeValue(obj, numVars, denVars, indicator.unit);
}

/**
 * Fetches county-level values for all counties in the state for the latest year.
 * Used to build stateGeography for the state choropleth toggle.
 * Returns [{ geoid, name, value }]
 */
async function fetchStateCountyGeography(indicator, year, geo) {
  const numVars = parseVars(indicator.numeratorVar);
  const denVars = parseVars(indicator.denominatorVar);
  const allVars = [...new Set([...numVars, ...denVars])];

  const params = new URLSearchParams({
    get: ['NAME', ...allVars].join(','),
    for: 'county:*',
    in: `state:${geo.state}`,
    ...(API_KEY ? { key: API_KEY } : {})
  });

  try {
    const rows = await cachedFetchJSON(`${CENSUS_BASE}/${year}/acs/acs5?${params}`);
    if (!Array.isArray(rows) || rows.length < 2) return [];
    const headers = rows[0];
    return rows.slice(1).map(row => {
      const obj = {};
      headers.forEach((h, i) => { obj[h] = row[i]; });
      return {
        geoid: `${obj.state}${obj.county}`,
        name: obj.NAME,
        value: computeValue(obj, numVars, denVars, indicator.unit)
      };
    }).filter(r => r.value !== null);
  } catch (_) {
    return [];
  }
}

/**
 * Hook: fetches all census indicators from config.
 *
 * geographyOverride modes:
 *   'group'  — fetches county aggregates for each county in the group, averages them
 *   'tracts' — fetches all tracts in the county, filters to selected tractGeoids
 *   default  — standard single-county tract fetch
 *
 * Returns:
 *   data: Map<indicatorId, {
 *     indicatorId, label, unit,
 *     geography,        — tract/county-level for latest year
 *     geographyByYear,  — Map<year, geography>
 *     trend,            — [{ year, value }] county/group aggregate per year
 *     stateTrend,       — [{ year, value }] state aggregate per year
 *     countyValue,      — latest year aggregate (derived from trend)
 *     stateValue,       — latest year state aggregate
 *     stateGeography    — [{ geoid, name, value }] all state counties (for state toggle)
 *   }>
 *   loading: boolean
 *   error: string | null
 */
export function useCensusData(config, overrideIndicators, geographyOverride) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const indicators = (overrideIndicators ?? config.indicators).filter(i => i.source === 'census');
    if (indicators.length === 0) {
      setData(new Map());
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const results = new Map();

        await Promise.all(indicators.map(async (indicator) => {
          const trendYears = indicator.years?.length > 0 ? indicator.years : [];
          const latestYear = trendYears.length > 0
            ? trendYears[trendYears.length - 1]
            : 2024;
          const yearsToFetch = trendYears.length > 0 ? trendYears : [latestYear];

          // ── Mode 2: county group ─────────────────────────────────────────────
          if (geographyOverride?.mode === 'group') {
            const { state: gState, counties: groupCounties } = geographyOverride;

            // For each year, fetch county aggregates for all counties in group
            const yearData = await Promise.all(
              yearsToFetch.map(async year => {
                const countyVals = await Promise.all(
                  groupCounties.map(({ fips }) =>
                    fetchAggregateValue(indicator, year, { state: gState, county: fips }, 'county')
                  )
                );
                const geoForYear = groupCounties
                  .map((c, i) => ({ geoid: `${gState}${c.fips}`, value: countyVals[i] }))
                  .filter(g => g.value !== null);
                const valid = countyVals.filter(v => v !== null);
                const avg = valid.length ? valid.reduce((s, v) => s + v, 0) / valid.length : null;
                return { year, geography: geoForYear, avg };
              })
            );

            const geographyByYear = new Map(yearData.map(({ year, geography }) => [year, geography]));
            const countyTrend = yearData.filter(d => d.avg !== null).map(({ year, avg }) => ({ year, value: avg }));
            const geographyData = geographyByYear.get(latestYear) ?? [];
            const countyValue = countyTrend.find(p => p.year === latestYear)?.value ?? null;

            results.set(indicator.id, {
              indicatorId: indicator.id, label: indicator.label, unit: indicator.unit,
              geography: geographyData, geographyByYear,
              stateGeography: [],
              trend: countyTrend, stateTrend: [],
              countyValue, stateValue: null
            });
            return;
          }

          // ── Mode 3: tract selection (with a non-empty list) ──────────────────
          if (geographyOverride?.mode === 'tracts' && geographyOverride.tractGeoids?.length > 0) {
            const tractGeoidsSet = new Set(geographyOverride.tractGeoids);
            const geo = config.org.geography;

            const yearData = await Promise.all(
              yearsToFetch.map(async year => {
                const allTracts = await fetchIndicatorYear(indicator, year, geo);
                // Only include selected tracts — non-selected get no value → grey on map
                const selectedTracts = allTracts.filter(t => tractGeoidsSet.has(t.geoid));
                const valid = selectedTracts.filter(t => t.value !== null);
                const avg = valid.length ? valid.reduce((s, t) => s + t.value, 0) / valid.length : null;
                return { year, tracts: selectedTracts, avg };
              })
            );

            const geographyByYear = new Map(yearData.map(({ year, tracts }) => [year, tracts]));
            const countyTrend = yearData.filter(d => d.avg !== null).map(({ year, avg }) => ({ year, value: avg }));
            const geographyData = geographyByYear.get(latestYear) ?? [];
            const countyValue = countyTrend.find(p => p.year === latestYear)?.value ?? null;

            const [stateTrend, stateGeography] = await Promise.all([
              Promise.all(yearsToFetch.map(async year => {
                const val = await fetchAggregateValue(indicator, year, geo, 'state');
                return val !== null ? { year, value: val } : null;
              })).then(arr => arr.filter(Boolean)),
              fetchStateCountyGeography(indicator, latestYear, geo)
            ]);
            const stateValue = stateTrend.find(p => p.year === latestYear)?.value ?? null;

            results.set(indicator.id, {
              indicatorId: indicator.id, label: indicator.label, unit: indicator.unit,
              geography: geographyData, geographyByYear,
              stateGeography,
              trend: countyTrend, stateTrend,
              countyValue, stateValue
            });
            return;
          }

          // ── Default: single county (Mode 1 or Mode 3 with no tract filter) ──
          const [geographyByYear, countyTrend, stateTrend, stateGeography] = await Promise.all([
            Promise.all(
              yearsToFetch.map(async year => {
                const data = await fetchIndicatorYear(indicator, year, config.org.geography);
                return [year, data];
              })
            ).then(entries => new Map(entries)),

            Promise.all(
              yearsToFetch.map(async (year) => {
                const val = await fetchAggregateValue(indicator, year, config.org.geography, 'county');
                return val !== null ? { year, value: val } : null;
              })
            ).then(arr => arr.filter(Boolean)),

            Promise.all(
              yearsToFetch.map(async (year) => {
                const val = await fetchAggregateValue(indicator, year, config.org.geography, 'state');
                return val !== null ? { year, value: val } : null;
              })
            ).then(arr => arr.filter(Boolean)),

            fetchStateCountyGeography(indicator, latestYear, config.org.geography)
          ]);

          const countyValue = countyTrend.find(p => p.year === latestYear)?.value ?? null;
          const stateValue  = stateTrend.find(p => p.year === latestYear)?.value ?? null;
          const geographyData = geographyByYear.get(latestYear) ?? [];

          results.set(indicator.id, {
            indicatorId: indicator.id,
            label: indicator.label,
            unit: indicator.unit,
            geography: geographyData,
            geographyByYear,
            stateGeography,
            trend: countyTrend,
            stateTrend,
            countyValue,
            stateValue
          });
        }));

        if (!cancelled) {
          setData(results);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(`Census data failed to load: ${err.message}`);
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [config, overrideIndicators, geographyOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error };
}
